"use client";

import { useState, useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/trpc/client";

type TimeRange = "30d" | "90d" | "1y" | "all";

interface ChainActiveWalletsChartProps {
  slug: string;
}

function formatCount(value: number): string {
  if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(0)}K`;
  return value.toLocaleString();
}

export function ChainActiveWalletsChart({ slug }: ChainActiveWalletsChartProps) {
  const [range, setRange] = useState<TimeRange>("90d");

  const days = range === "30d" ? 30 : range === "90d" ? 90 : range === "1y" ? 365 : 365;

  const { data: rawData, isLoading } = api.onchain.getActiveWallets.useQuery(
    { slug, days },
    { staleTime: 5 * 60 * 1000 },
  );

  const filteredData = useMemo(() => {
    if (!rawData) return [];
    if (range === "all") return rawData;
    const now = Date.now();
    const cutoffDays = range === "30d" ? 30 : range === "90d" ? 90 : 365;
    const cutoff = now - cutoffDays * 86400 * 1000;
    return rawData.filter((d) => new Date(d.date).getTime() >= cutoff);
  }, [rawData, range]);

  const ranges: { key: TimeRange; label: string }[] = [
    { key: "30d", label: "30D" },
    { key: "90d", label: "90D" },
    { key: "1y", label: "1Y" },
    { key: "all", label: "All" },
  ];

  if (isLoading) {
    return (
      <div className="h-[300px] flex items-center justify-center text-gray-500">
        Loading active wallet data...
      </div>
    );
  }

  if (!filteredData.length) {
    return (
      <div className="h-[300px] flex items-center justify-center text-gray-500">
        No active wallet data available yet. Data builds up over time from daily syncs.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-1 mb-4">
        {ranges.map((r) => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
              range === r.key
                ? "bg-blue-600 text-white"
                : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={filteredData}>
            <defs>
              <linearGradient id="walletsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tickFormatter={(d: string) => {
                const date = new Date(d);
                return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
              }}
              stroke="#4b5563"
              tick={{ fill: "#6b7280", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              minTickGap={40}
            />
            <YAxis
              tickFormatter={formatCount}
              stroke="#4b5563"
              tick={{ fill: "#6b7280", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={70}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(230,15%,12%)",
                border: "1px solid hsl(230,15%,20%)",
                borderRadius: "8px",
                color: "#fff",
                fontSize: "13px",
              }}
              labelFormatter={(d) =>
                new Date(String(d)).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })
              }
              formatter={(value) => [formatCount(Number(value)), "Active Addresses"]}
            />
            <Area
              type="monotone"
              dataKey="activeAddresses"
              stroke="#8b5cf6"
              strokeWidth={2}
              fill="url(#walletsGradient)"
              dot={false}
              animationDuration={800}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
